import { css } from '@emotion/css';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AspectRatioImage } from 'src/components/shared/AspectRatioImage';
import { sendEvent } from 'src/lib/google-analytics';
import { UserResortProfile } from 'src/models/resort/UserResortProfile';
import { ForDetailLink } from './ForDetailLink';
import { LockedProfile } from './LockedProfile';

interface ResortDeepLinkLockedProps {
  profile: UserResortProfile;
  isMyself: boolean;
  hasAoc: boolean;
  country: string;
}

export const ResortDeepLinkLocked: React.FC<ResortDeepLinkLockedProps> = ({
  profile,
  isMyself,
  hasAoc,
  country
}) => {
  const { t } = useTranslation('resort');
  const { i18n } = useTranslation();

  useEffect(() => {
    if (hasAoc) {
      sendEvent('resort_locked_deep_link', 'display', 'locked_resort_app_net');
    } else {
      sendEvent('resort_locked_deep_link', 'display', 'no_aoc');
    }
  }, [hasAoc]);

  const url = hasAoc ? undefined : externalLink(country, i18n.language);

  return (
    <div
      className={css`
        max-width: 480px;
        margin: 0 auto;
        padding: 0 16px 40px;
      `}
    >
      <LockedProfile profile={profile} isMyself={isMyself} />
      <div
        className={css`
          color: #4d3906;
          text-align: center;
          h2 {
            font-size: 20px;
            margin: 20px auto;
          }
          section {
            margin: 46px 0 24px;
          }
          section p {
            font-size: 14px;
            margin: 24px auto;
          }
          .image {
            margin: 0 auto;
          }
          .paper {
            position: relative;
            background: #fefff5;
            box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.25);
            border-radius: 1px;
            padding: 20px 16px 12px;
          }
          .pin {
            display: flex;
            justify-content: center;
            position: absolute;
            top: -16px;
            left: 0;
            right: 0;
          }
          .unlock-conditions {
            display: flex;
            flex-direction: column;
            list-style: auto;
            font-size: 14px;
            padding: 0 0 0 24px;
            li {
              text-align: left;
              text-align: start;
            }
          }
          .for-detail {
            text-align: center;
            margin-bottom: 40px;
          }
        `}
      >
        <section className="paper">
          <div className="pin">
            <img
              srcSet="/images/resort/dpl/img-tsukutter-pin-2x.png 2x, /images/resort/dpl/img-tsukutter-pin.png 1x"
              alt=""
            />
          </div>
          <h2>{t('resort_locked')}</h2>
          <div className="image">
            <AspectRatioImage
              srcSet={`
                /images/resort/hint/img_tsukutter_hint_01-2x.png 2x,
                /images/resort/hint/img_tsukutter_hint_01.png 1x
              `}
              width={320}
              height={201}
            />
          </div>
          <p>{t('resort_locked_0')}</p>
          <p>{t('resort_locked_1')}</p>
        </section>

        <section>
          <h2>{t('how_to_unlock')}</h2>
          <p>{t('how_to_unlock_desc_0')}</p>
          <ul className="unlock-conditions">
            <li>{t('unlock_condition_0')}</li>
            <li>{t('unlock_condition_1')}</li>
            <li>{t('unlock_condition_2')}</li>
          </ul>
        </section>

        {url && (
          <div className="for-detail">
            <ForDetailLink url={url} />
          </div>
        )}
      </div>
    </div>
  );
};

function externalLink(naCountry: string, lang: string): string | undefined {
  switch (naCountry) {
    case 'JP':
      return 'https://store-jp.nintendo.com/list/software/70050000029680.html';
    case 'US':
    case 'CA':
    case 'AR':
    case 'CO':
    case 'CL':
    case 'PE':
    case 'BR':
    case 'MX':
      return 'https://ec.nintendo.com/aoc_purchase_confirm?aoc=70050000030669';
    case 'IE':
    case 'GB':
      return 'https://www.nintendo.co.uk/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'IT':
      return 'https://www.nintendo.it/Giochi/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'AT':
      return 'https://www.nintendo.at/Spiele/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'NL':
      return 'https://www.nintendo.nl/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'CH':
      if (lang === 'de-DE') {
        return 'https://www.nintendo.ch/de/Spiele/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else if (lang === 'fr-FR') {
        return 'https://www.nintendo.ch/fr/Jeux/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else if (lang === 'it-IT') {
        return 'https://www.nintendo.ch/it/Giochi/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else {
        return 'https://www.nintendo.co.uk/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      }
    case 'ES':
      return 'https://www.nintendo.es/Juegos/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'DE':
      return 'https://www.nintendo.de/Spiele/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'FR':
      return 'https://www.nintendo.fr/Jeux/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'BE':
      if (lang === 'fr-FR') {
        return 'https://www.nintendo.be/fr/Jeux/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else if (lang === 'nl-NL') {
        return 'https://www.nintendo.be/nl/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else {
        return 'https://www.nintendo.co.uk/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      }
    case 'PT':
      return 'https://www.nintendo.pt/Jogos/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'RU':
      return 'https://www.nintendo.ru/-/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'ZA':
      return 'https://www.nintendo.co.za/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
    case 'LU':
      if (lang === 'fr-FR') {
        return 'https://www.nintendo.be/fr/Jeux/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else if (lang === 'de-DE') {
        return 'https://www.nintendo.de/Spiele/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      } else {
        return 'https://www.nintendo.co.uk/Games/Nintendo-Switch/Animal-Crossing-New-Horizons-1438623.html#Happy_Home_Paradise';
      }
    default:
      return undefined;
  }
}
